import React, {useCallback, useEffect, useState} from 'react';
import {Link} from 'react-router-dom';
import {
    DeclutterPendingItem,
    DeclutterPhysicalStep as PhysicalStep,
    confirmDisposal,
    fetchPending,
    returnToWardrobe,
} from '../service/DeclutterApi';
import {bucketActionLabel} from '../support/declutterLabels';
import {DISPOSAL_METHOD_LABELS, DisposalIntent, INTENT_OPTIONS} from '../support/itemLifecycle';
import AuthImage from './AuthImage';
import {ShirtIcon} from './icons';
import Spinner from './Spinner';
import ConfirmDisposalDialog from './disposal/ConfirmDisposalDialog';

/**
 * «Следующий физический шаг»: решения приняты, дело за руками.
 *
 * <p>Экран существует потому, что решение и действие — разные события. Пока вещи лежат в пакете
 * у шкафа, место не освобождено, поэтому здесь нет ни объёма, ни веса: они появляются в отчёте
 * только после подтверждения.</p>
 *
 * <p>Список читают стоя у шкафа, поэтому строка даёт приметы вещи — миниатюру, бренд и размер —
 * и ссылку на карточку. По одним названиям нужную чёрную толстовку в стопке не находят.</p>
 */

/** Способ по умолчанию для направления физического шага. */
const BUCKET_METHOD: Record<string, DisposalIntent> = {
    TO_DONATE: 'DONATED',
    TO_DISPOSE: 'DISPOSED',
};

/** Сегодняшнее число в формате input[type=date] — и значение по умолчанию, и потолок. */
const today = (): string => new Date().toISOString().slice(0, 10);

export default function DeclutterPhysicalStep({ownerId, onChanged, data: controlled}: {
    ownerId: string | null;
    onChanged?: () => void;
    /** Данные из стора страницы; без них компонент загружает очередь сам. */
    data?: PhysicalStep | null;
}) {
    const [own, setOwn] = useState<PhysicalStep | null>(null);
    const [selected, setSelected] = useState<Set<string>>(new Set());
    const [loading, setLoading] = useState(controlled === undefined);
    const [busy, setBusy] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [date, setDate] = useState(today);
    const [confirming, setConfirming] = useState<{ids: string[]; method: DisposalIntent; names: string[]} | null>(null);
    const data = controlled === undefined ? own : controlled;

    const load = useCallback(async () => {
        setSelected(new Set());
        if (controlled !== undefined) {
            return;
        }
        setLoading(true);
        setError(null);
        try {
            setOwn(await fetchPending(ownerId));
        } catch {
            setError('Не удалось загрузить очередь на выбытие');
        } finally {
            setLoading(false);
        }
    }, [ownerId, controlled]);

    useEffect(() => {
        void load();
    }, [load]);

    const toggle = (itemId: string) => {
        setSelected(prev => {
            const next = new Set(prev);
            if (next.has(itemId)) {
                next.delete(itemId);
            } else {
                next.add(itemId);
            }
            return next;
        });
    };

    const askConfirm = (method: DisposalIntent) => {
        if (!data || selected.size === 0) return;
        const ids = Array.from(selected);
        const names = data.items.filter(i => ids.includes(i.itemId)).map(i => i.name);
        setConfirming({ids, method, names});
    };

    const runConfirm = async () => {
        if (!confirming) return;
        const {ids, method} = confirming;
        setConfirming(null);
        setBusy(true);
        setError(null);
        try {
            await confirmDisposal(ownerId, ids, method, date);
            await load();
            onChanged?.();
        } catch {
            setError('Не удалось подтвердить выбытие');
        } finally {
            setBusy(false);
        }
    };

    const runReturn = async () => {
        if (selected.size === 0) return;
        setBusy(true);
        setError(null);
        try {
            await returnToWardrobe(ownerId, Array.from(selected));
            await load();
            onChanged?.();
        } catch {
            setError('Не удалось вернуть вещи в гардероб');
        } finally {
            setBusy(false);
        }
    };

    if (loading) {
        return <div className="flex justify-center py-8"><Spinner/></div>;
    }

    if (!data || data.items.length === 0) {
        return (
            <div className="card p-6 text-center" data-testid="declutter-pending-empty">
                <p className="text-ink-700 dark:text-ink-200">
                    Ничего не ждёт выноса — все решения доведены до конца.
                </p>
            </div>
        );
    }

    const grouped = data.items.reduce<Record<string, DeclutterPendingItem[]>>((acc, item) => {
        (acc[item.bucket] ??= []).push(item);
        return acc;
    }, {});
    // Способ по умолчанию — направление той группы, из которой выбрана первая вещь: чаще всего
    // подтверждают одну группу целиком, и угадывать полезнее, чем заставлять выбирать заново.
    const firstSelected = data.items.find(i => selected.has(i.itemId));
    const defaultMethod: DisposalIntent = firstSelected?.intent && firstSelected.intent !== 'RECYCLED'
        ? firstSelected.intent
        : BUCKET_METHOD[firstSelected?.bucket ?? ''] ?? 'DISPOSED';

    return (
        <div className="card p-4" data-testid="declutter-physical-step">
            {confirming && (
                <ConfirmDisposalDialog
                    method={confirming.method}
                    names={confirming.names}
                    date={date}
                    onConfirm={() => void runConfirm()}
                    onCancel={() => setConfirming(null)}
                />
            )}
            <div className="flex items-baseline justify-between gap-3 flex-wrap">
                <h3 className="text-sm font-medium text-ink-900 dark:text-ink-100">
                    Следующий физический шаг
                </h3>
                {data.oldestHeldDays !== null && data.oldestHeldDays > 0 && (
                    <span className="text-xs text-amber-600 dark:text-amber-400"
                          data-testid="declutter-oldest-held">
                        Самая старая вещь ждёт {data.oldestHeldDays} дн.
                    </span>
                )}
            </div>
            {error && <p className="mt-2 text-sm text-red-600 dark:text-red-400" role="alert">{error}</p>}

            {Object.entries(grouped).map(([bucket, items]) => (
                <section key={bucket} className="mt-4">
                    <h4 className="text-sm font-medium text-ink-800 dark:text-ink-200">
                        {bucketActionLabel(bucket)} · {items.length}
                    </h4>
                    <ul className="mt-1 divide-y divide-ink-100 dark:divide-ink-700">
                        {items.map(item => (
                            <li key={item.itemId} data-testid="declutter-pending-row"
                                className="py-2 flex items-center gap-3">
                                <input
                                    type="checkbox"
                                    className="w-5 h-5 shrink-0 accent-brand-600"
                                    checked={selected.has(item.itemId)}
                                    onChange={() => toggle(item.itemId)}
                                    aria-label={`Выбрать ${item.name}`}
                                />
                                {item.primaryPhotoUrl ? (
                                    <AuthImage imageKey={item.primaryPhotoUrl} alt={item.name} variant="thumbnail"
                                               className="w-12 h-12 rounded-lg object-cover shrink-0"/>
                                ) : (
                                    <div className="w-12 h-12 rounded-lg bg-ink-100 dark:bg-ink-700 shrink-0
                                                    flex items-center justify-center text-ink-400">
                                        <ShirtIcon className="w-6 h-6"/>
                                    </div>
                                )}
                                <div className="min-w-0 flex-1">
                                    <Link to={`/clothing/${item.itemId}`}
                                          className="text-sm text-ink-800 dark:text-ink-200 hover:text-brand-600
                                                     dark:hover:text-brand-400 hover:underline break-words">
                                        {item.name}
                                    </Link>
                                    <p className="text-xs text-ink-500 dark:text-ink-400 break-words">
                                        {[item.brandName, item.sizeName && `размер ${item.sizeName}`,
                                            item.categoryName].filter(Boolean).join(' · ') || 'Без описания'}
                                    </p>
                                </div>
                                {item.heldDays !== null && (
                                    <span className="text-xs text-ink-500 shrink-0">{item.heldDays} дн.</span>
                                )}
                            </li>
                        ))}
                    </ul>
                </section>
            ))}

            <div className="mt-4 flex flex-wrap items-end gap-3">
                <label className="text-xs text-ink-500 dark:text-ink-400">
                    <span className="block mb-1">Дата события</span>
                    <input type="date" value={date} max={today()} onChange={e => setDate(e.target.value)}
                           data-testid="declutter-confirm-date" className="input-field min-h-[44px]"/>
                </label>
                <button type="button" className="btn-accent min-h-[44px] px-4 rounded-xl"
                        data-testid="declutter-confirm" disabled={busy || selected.size === 0}
                        onClick={() => askConfirm(defaultMethod)}>
                    {DISPOSAL_METHOD_LABELS[defaultMethod]} ({selected.size})
                </button>
                <button type="button" className="btn-secondary min-h-[44px] px-4 rounded-xl"
                        data-testid="declutter-return" disabled={busy || selected.size === 0}
                        onClick={() => void runReturn()}>
                    Вернуть в гардероб
                </button>
            </div>
            <div className="mt-2 flex flex-wrap items-center gap-2 text-xs text-ink-500 dark:text-ink-400">
                <span>Другой способ:</span>
                {INTENT_OPTIONS.filter(o => o.value !== defaultMethod).map(o => (
                    <button key={o.value} type="button" disabled={busy || selected.size === 0}
                            onClick={() => askConfirm(o.value)}
                            data-testid={`declutter-confirm-${o.value}`}
                            className="min-h-[44px] px-2 rounded-lg text-brand-600 dark:text-brand-400
                                       hover:underline disabled:opacity-50">
                        {DISPOSAL_METHOD_LABELS[o.value]}
                    </button>
                ))}
            </div>
            <p className="mt-2 text-xs text-ink-500 dark:text-ink-400"
               data-testid="declutter-physical-irreversible">
                Подтверждение фиксирует факт: вещь покидает гардероб, её карточка закрывается,
                история остаётся. Отменить подтверждение нельзя.
            </p>
        </div>
    );
}
